import {useContext, MutableRefObject, useRef} from 'react';
import {Box} from '@mui/material';
import {Switch, Route, Redirect, useRouteMatch} from 'react-router-dom';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {AuthContext} from '../../../../../contexts/auth/context';
import {AuthContextType} from '../../../../../contexts/auth/types';
import {useAppContext} from '../../../../../contexts/app/context';
import {Environment} from '../../../../../enums';
import ActivityInformalCaregiverOfficeScreen from '../activity/activity.informal-caregiver.screen';
import ReminderInformalCaregiverOfficeScreen from '../reminder/reminder.informal-caregiver.screen';
import ApplicationsInformalCaregiverScreen from '../applications/applications.informal-caregiver.screen';
import MediaInformalCaregiverOfficeScreen from '../media/media.informal-caregiver.screen';
import ProfileInformalCaregiverOfficeScreen from '../profile/profile.informal-caregiver.screen';
import ServicesInformalCaregiverScreen from '../services/services.informal-caregiver.screen';

export default function InformalCaregiverOffice() {
  // Get context
  const authContext: AuthContextType = useContext(AuthContext);
  const appContext = useAppContext();
  const canAccessDevelopmentFeatures =
    appContext.state.configuration.environment === Environment.Development;

  // Hold ref
  const selectedSeniorIdRef: MutableRefObject<string | undefined> = useRef(
    authContext.state.session?.selectedRelationship?.withUser.id
  );

  // Update ref
  const updateSelectedSeniorIdRef = () => {
    // Update it
    selectedSeniorIdRef.current = authContext.state.session?.selectedRelationship?.withUser.id;
  };

  // Routing
  let {path} = useRouteMatch();

  return (
    <Box>
      <Switch>
        {/** Senior activity */}
        <Route
          exact
          path={`${path}/:seniorId${ScreenPaths.InformalCaregiver_Activity}`}
          render={({
            match: {
              params: {seniorId},
            },
          }) => {
            if (seniorId === authContext.state.session?.selectedRelationship?.withUser.id) {
              // Update it
              updateSelectedSeniorIdRef();

              // Nothing changed
              return <ActivityInformalCaregiverOfficeScreen />;
            } else {
              // Update it
              updateSelectedSeniorIdRef();

              // Always redirect to the activity screen again
              return (
                <Redirect
                  to={`${path}/${authContext.state.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`}
                />
              );
            }
          }}
        />
        {canAccessDevelopmentFeatures && (
          <Route
            exact
            path={`${path}/:seniorId${ScreenPaths.InformalCaregiver_Medias}/:mediaId?`}
            render={({
              match: {
                params: {seniorId},
              },
            }) => {
              const selectedSeniorId = authContext.state.session?.selectedRelationship?.withUser.id;
              if (!selectedSeniorId) return null;

              if (seniorId === selectedSeniorId) {
                updateSelectedSeniorIdRef();
                return <MediaInformalCaregiverOfficeScreen key={selectedSeniorId} />;
              }

              const seniorChanged = selectedSeniorIdRef.current !== selectedSeniorId;
              updateSelectedSeniorIdRef();
              return (
                <Redirect
                  to={`${path}/${selectedSeniorId}${
                    seniorChanged
                      ? ScreenPaths.InformalCaregiver_Medias
                      : ScreenPaths.InformalCaregiver_Activity
                  }`}
                />
              );
            }}
          />
        )}
        {canAccessDevelopmentFeatures && (
          <Route
            exact
            path={`${path}/:seniorId${ScreenPaths.InformalCaregiver_Reminders}`}
            render={({
              match: {
                params: {seniorId},
              },
            }) => {
              const selectedSeniorId = authContext.state.session?.selectedRelationship?.withUser.id;

              if (seniorId === selectedSeniorId) {
                updateSelectedSeniorIdRef();
                return <ReminderInformalCaregiverOfficeScreen />;
              }

              if (selectedSeniorIdRef.current !== selectedSeniorId) {
                updateSelectedSeniorIdRef();
                return (
                  <Redirect
                    to={`${path}/${selectedSeniorId}${ScreenPaths.InformalCaregiver_Reminders}`}
                  />
                );
              }

              updateSelectedSeniorIdRef();
              return (
                <Redirect
                  to={`${path}/${selectedSeniorId}${ScreenPaths.InformalCaregiver_Activity}`}
                />
              );
            }}
          />
        )}
        {canAccessDevelopmentFeatures && (
          <Route
            exact
            path={[
              `${path}/:seniorId${ScreenPaths.InformalCaregiver_Applications}`,
              `${path}/:seniorId${ScreenPaths.InformalCaregiver_Application}`,
            ]}
            render={({
              match: {
                params: {seniorId},
              },
            }) => {
              const selectedId = authContext.state.session?.selectedRelationship?.withUser.id;
              if (!selectedId) return null;
              if (seniorId !== selectedId) {
                return (
                  <Redirect
                    to={`${path}/${selectedId}${ScreenPaths.InformalCaregiver_Applications}`}
                  />
                );
              }
              return <ApplicationsInformalCaregiverScreen />;
            }}
          />
        )}
        {canAccessDevelopmentFeatures && (
          <Route
            exact
            path={`${path}/:seniorId${ScreenPaths.InformalCaregiver_Profile}`}
            render={({
              match: {
                params: {seniorId},
              },
            }) => {
              const selectedSeniorId = authContext.state.session?.selectedRelationship?.withUser.id;
              if (!selectedSeniorId) return null;
              if (seniorId === selectedSeniorId) {
                updateSelectedSeniorIdRef();
                return (
                  <ProfileInformalCaregiverOfficeScreen
                    key={selectedSeniorId}
                    seniorId={selectedSeniorId}
                  />
                );
              }
              const seniorChanged = selectedSeniorIdRef.current !== selectedSeniorId;
              updateSelectedSeniorIdRef();
              return (
                <Redirect
                  to={`${path}/${selectedSeniorId}${seniorChanged ? ScreenPaths.InformalCaregiver_Profile : ScreenPaths.InformalCaregiver_Activity}`}
                />
              );
            }}
          />
        )}

        {canAccessDevelopmentFeatures && (
          <Route
            exact
            path={`${path}/:seniorId${ScreenPaths.InformalCaregiver_Services}/:entityId?/:serviceId?`}
            render={({
              match: {
                params: {seniorId},
              },
            }) => {
              const selectedId = authContext.state.session?.selectedRelationship?.withUser.id;
              if (!selectedId) return null;
              if (seniorId === selectedId) {
                updateSelectedSeniorIdRef();
                return <ServicesInformalCaregiverScreen key={selectedId} seniorId={selectedId} />;
              }
              const changed = selectedSeniorIdRef.current !== selectedId;
              updateSelectedSeniorIdRef();
              return (
                <Redirect
                  to={`${path}/${selectedId}${changed ? ScreenPaths.InformalCaregiver_Services : ScreenPaths.InformalCaregiver_Activity}`}
                />
              );
            }}
          />
        )}
        {/** Default Route */}
        <Route path={path}>
          <Redirect
            to={`${path}/${authContext.state.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`}
          />
        </Route>
      </Switch>
    </Box>
  );
}
